import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Github, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { supabase } from '../../config/supabase'
import EagleBackToTop from './EagleBackToTop'
import PortfolioHero from './PortfolioHero'
import PortfolioPageLink from './PortfolioPageLink'
import { buildingProjects, clientProjects } from './portfolioProjects'
import './portfolio-work.css'

const smallTools = [
  {
    name: 'Real-Time Money Counter',
    description: 'Tracks how much you earn in real time based on your hourly rate. Runs in the browser, no data sent anywhere.',
    href: '/money_counter.html',
    stack: ['HTML', 'CSS', 'Vanilla JS'],
  },
  {
    name: 'Singapore CPF Calculator',
    description: 'CPF contribution breakdown with projections and charts. Built for Singapore salaries.',
    href: '/CPF.html',
    stack: ['HTML', 'CSS', 'Vanilla JS', 'Chart.js'],
  },
]

const POSTS_PER_PAGE = 5

// Static fallback posts — shown if Supabase has nothing yet
const staticPosts = [
  { slug: 'css-tailwind-shadcn-how-they-fit-together', title: 'CSS, Tailwind, Radix, shadcn — How They Actually Fit Together', date: 'Mar 2026', readTime: '6 min' },
  { slug: 'how-api-endpoints-work', title: 'How API Endpoints Work', date: 'Feb 2026', readTime: '4 min' },
]

function sortPostsByDateDesc<T extends { date: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

const notes = [
  {
    slug: '/api-explained.html',
    title: 'How API Endpoints Work',
    summary: 'The office building analogy. HTTPS is the armored car, the port is the front door, your access token is a visitor badge that expires.',
    tags: ['APIs', 'Auth', 'HTTP'],
    date: 'Feb 2026',
  },
  {
    slug: '/cybersecurity-101.html',
    title: 'Cybersecurity 101',
    summary: 'Phishing, SQL injection, brute force, MITM. Covers 20 terms with analogies, an attack simulator, and interactive diagrams for encryption and firewalls.',
    tags: ['Security', 'Networking', 'Fundamentals'],
    date: 'Mar 2026',
  },
]

const socialLinks = [
  { label: 'GitHub',   href: 'https://github.com/Eaglibr1um', icon: Github,   handle: '@Eaglibr1um' },
  //{ label: 'LinkedIn', href: 'https://linkedin.com',           icon: Linkedin, handle: 'Jax'         },
]

// ─── 4-tier text hierarchy (all WCAG AAA on #000000 and #0D1117) ─────────────
//  text-white      21:1 / 21:1   — name, strong headings
//  text-jax-body  14.5:1 / 13.1:1 — body paragraphs, descriptions
//  text-jax-link   8.2:1 /  7.4:1 — tagline, links, interactive accent
//  text-jax-subtle 8.4:1 /  7.5:1 — metadata: dates, handles, labels, section headers

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; onDoubleClick?: () => void }> = ({ children, onDoubleClick }) => (
  <div className="flex items-center gap-3 mb-8" onDoubleClick={onDoubleClick}>
    <span className="block w-5 h-px bg-jax-link shrink-0" aria-hidden="true" />
    <h2 className="text-[11px] font-mono text-jax-subtle uppercase tracking-[0.25em] select-none">
      {children}
    </h2>
  </div>
)

const Divider = () => <div className="border-t border-jax-navy mb-16" />

// ─── Project Card ─────────────────────────────────────────────────────────────

interface ProjectCardProps {
  name: string
  description: string
  href: string
  stack: string[]
  /** Project write-ups and standalone tools have different link labels. */
  detail?: boolean
}

const ProjectCard: React.FC<ProjectCardProps> = ({ name, description, href, stack, detail }) => {
  // Paths with a file extension (.html etc.) are static files - open with <a>, not React Router <Link>
  const isInternal = href.startsWith('/') && !href.includes('.')
  const isExternal = href.startsWith('http') || (href.startsWith('/') && href.includes('.'))
  const isClickable = isInternal || isExternal

  const cardClass = 'portfolio-project-row' + (isClickable ? ' is-clickable' : '')

  const inner = (
    <>
      <div className="portfolio-project-heading">
        <h3>{name}</h3>
        {isClickable && <span className="portfolio-project-action" aria-hidden="true">{detail ? 'Read project' : 'Open tool'}</span>}
      </div>
      <p className="portfolio-project-summary">{description}</p>
      <ul className="portfolio-project-stack" aria-label="Technologies">
        {stack.map(tag => <li key={tag}>{tag}</li>)}
      </ul>
    </>
  )

  if (isInternal) {
    return detail
      ? <PortfolioPageLink to={href} className={cardClass}>{inner}</PortfolioPageLink>
      : <Link to={href} className={cardClass}>{inner}</Link>
  }
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cardClass}>
        {inner}
      </a>
    )
  }
  return <div className={cardClass}>{inner}</div>
}

// ─── Post Row ─────────────────────────────────────────────────────────────────

interface PostRowProps {
  slug: string
  title: string
  date: string
  readTime: string
}

const PostRow: React.FC<PostRowProps> = ({ slug, title, date, readTime }) => (
  <Link
    to={`/writing/${slug}`}
    className={[
      'group relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-0 py-4',
      'border-b border-jax-navy last:border-b-0',
      '-mx-5 px-5',
      'before:absolute before:left-0 before:inset-y-0 before:w-0.5',
      'before:bg-jax-link before:scale-y-0 group-hover:before:scale-y-100',
      'before:transition-transform before:duration-200 before:origin-center',
      'transition-colors duration-200',
      'focus-visible:outline-none focus-visible:before:scale-y-100',
    ].join(' ')}
  >
    <div className="flex items-start gap-3 min-w-0 flex-1">
      <ArrowUpRight
        className="w-3.5 h-3.5 text-jax-subtle shrink-0 mt-0.5 sm:mt-0 group-hover:text-jax-link transition-colors duration-200"
      />
      <span className="text-jax-body text-sm group-hover:text-white transition-colors duration-200 line-clamp-2 break-words">
        {title}
      </span>
    </div>
    <div className="flex items-center gap-3 sm:gap-4 sm:ml-6 shrink-0 pl-[22px] sm:pl-0">
      <span className="text-jax-subtle italic font-normal text-[11px]">{readTime}</span>
      <span className="text-[10px] font-mono text-jax-subtle">{date}</span>
    </div>
  </Link>
)

// ─── Link Item ────────────────────────────────────────────────────────────────

interface LinkItemProps {
  label: string
  href: string
  handle: string
  icon: React.ElementType
}

const LinkItem: React.FC<LinkItemProps> = ({ label, href, handle, icon: Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={[
      'group relative flex items-center gap-3 py-3.5',
      'border-b border-jax-navy last:border-b-0',
      '-mx-5 px-5',
      'before:absolute before:left-0 before:inset-y-0 before:w-0.5',
      'before:bg-jax-link before:scale-y-0 group-hover:before:scale-y-100',
      'before:transition-transform before:duration-200 before:origin-center',
      'transition-colors duration-200',
      'focus-visible:outline-none focus-visible:before:scale-y-100',
    ].join(' ')}
  >
    <Icon className="w-4 h-4 text-jax-subtle group-hover:text-jax-link transition-colors duration-200 shrink-0" />
    <span className="text-jax-body text-sm group-hover:text-white transition-colors duration-200 font-medium">
      {label}
    </span>
    <span className="text-[11px] font-mono text-jax-subtle">{handle}</span>
    <ArrowUpRight className="w-3 h-3 text-jax-subtle ml-auto group-hover:text-jax-link transition-colors duration-200" />
  </a>
)

// ─── Page ─────────────────────────────────────────────────────────────────────

type PostRow = { slug: string; title: string; date: string; read_time: string }

const About: React.FC = () => {
  const navigate = useNavigate()
  const { hash } = useLocation()
  const [livePosts, setLivePosts] = useState<PostRow[]>([])

  useLayoutEffect(() => {
    // Returning from a project should land on its list, including with client-side routing.
    const style = document.documentElement.style
    const previous = style.getPropertyValue('scroll-behavior')
    const priority = style.getPropertyPriority('scroll-behavior')
    style.setProperty('scroll-behavior', 'auto', 'important')
    const section = hash ? document.getElementById(hash.slice(1)) : null
    if (section) section.scrollIntoView({ behavior: 'auto', block: 'start' })
    else window.scrollTo({ top: 0, behavior: 'auto' })
    if (previous) style.setProperty('scroll-behavior', previous, priority)
    else style.removeProperty('scroll-behavior')
  }, [hash])

  useEffect(() => {
    const prev = document.title
    document.title = 'About — Jax'
    return () => { document.title = prev }
  }, [])

  useEffect(() => {
    supabase
      .from('posts')
      .select('slug, title, date, read_time')
      .eq('published', true)
      .order('date', { ascending: false })
      .then(({ data }) => { if (data && data.length > 0) setLivePosts(data) })
  }, [])

  // Use Supabase posts if available, otherwise show static fallback; sort by date desc
  const allPosts = useMemo(() => {
    const raw = livePosts.length > 0
      ? livePosts.map((p) => ({ slug: p.slug, title: p.title, date: p.date, readTime: p.read_time }))
      : staticPosts
    return sortPostsByDateDesc(raw)
  }, [livePosts])

  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 320)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const [writingPage, setWritingPage] = useState(1)
  const totalWritingPages = Math.ceil(allPosts.length / POSTS_PER_PAGE)
  const paginatedPosts = useMemo(
    () => allPosts.slice((writingPage - 1) * POSTS_PER_PAGE, writingPage * POSTS_PER_PAGE),
    [allPosts, writingPage]
  )

  // Reset to page 1 when post list changes (e.g. after fetch)
  useEffect(() => {
    setWritingPage(1)
  }, [allPosts.length])

  return (
  <div
    className="min-h-screen bg-jax-bg text-white"
    style={{ fontFamily: '"Source Sans Pro", system-ui, sans-serif' }}
  >
    {/* Skip to content — visible only on keyboard focus */}
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 bg-jax-surface border border-jax-link text-jax-link text-sm font-mono px-4 py-2 rounded focus:outline-none"
    >
      Skip to content
    </a>

    {/* Top accent bar */}
    <div className="h-[2px] bg-jax-accent" />

    {/* Nav */}
    <header className="border-b border-jax-navy">
      <nav aria-label="Site navigation" className="portfolio-navigation">
        <span className="font-mono text-[11px] text-jax-subtle tracking-widest uppercase select-none">
          jax.dev
        </span>
        <div className="portfolio-navigation-links">
        <a href="#building">Work</a>
        <a href="#client">Clients</a>
        <a href="#writing">Writing</a>
        <a href="#about" className="portfolio-navigation-about">About</a>
        <a
          href="https://github.com/Eaglibr1um"
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub ↗
        </a>
        </div>
      </nav>
    </header>

    <main id="main-content" tabIndex={-1} className="pb-24">
      <PortfolioHero />
      <div className="portfolio-content max-w-[680px] mx-auto px-6">
      <Divider />

      {/* ── About ─────────────────────────────────────────────── */}
      <section id="about" className="mb-16">
        <SectionLabel>About</SectionLabel>
        <div className="space-y-4 text-jax-body text-sm sm:text-base leading-relaxed">
          <p>
            I studied BioScience. Now I work at a startup building software with Cursor IDE.
            The gap between where I started and where I need to be is real but I am working on it.
          </p>
          <p>
            This site is where I document that process. Things I build, concepts I figure out,
            and the mistakes worth writing about.
          </p>
        </div>
      </section>

      <Divider />

      {/* ── Writing ───────────────────────────────────────────── */}
      <section id="writing" className="mb-16">
        <SectionLabel onDoubleClick={() => navigate('/editor?tab=prompt')}>Writing</SectionLabel>
        <div className="border border-jax-navy bg-jax-surface rounded-lg overflow-hidden px-5">
          {paginatedPosts.map((post) => (
            <PostRow key={post.slug} {...post} />
          ))}
          {totalWritingPages > 1 && (
            <div className="flex items-center justify-between py-4 border-t border-jax-navy">
              <button
                type="button"
                onClick={() => setWritingPage((p) => Math.max(1, p - 1))}
                disabled={writingPage <= 1}
                className="flex items-center gap-1.5 text-jax-subtle hover:text-jax-link disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-mono focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-jax-link rounded px-1 py-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-[11px] font-mono text-jax-subtle">
                {writingPage} / {totalWritingPages}
              </span>
              <button
                type="button"
                onClick={() => setWritingPage((p) => Math.min(totalWritingPages, p + 1))}
                disabled={writingPage >= totalWritingPages}
                className="flex items-center gap-1.5 text-jax-subtle hover:text-jax-link disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-mono focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-jax-link rounded px-1 py-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      <Divider />

      {/* ── Currently Building ────────────────────────────────── */}
      <section id="building" className="mb-16">
        <SectionLabel>Personal projects</SectionLabel>
        <div className="portfolio-project-list">
          {buildingProjects.map((project) => (
            <ProjectCard
              key={project.slug}
              name={project.name}
              description={project.summary}
              href={`/about/work/${project.slug}`}
              stack={project.stack}
              detail
            />
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Client Work ───────────────────────────────────────── */}
      <section id="client" className="mb-16">
        <SectionLabel>Client Work</SectionLabel>
        <div className="portfolio-project-list">
          {clientProjects.map((project) => (
            <ProjectCard
              key={project.slug}
              name={project.name}
              description={project.summary}
              href={`/about/work/${project.slug}`}
              stack={project.stack}
              detail
            />
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Small Tools ───────────────────────────────────────── */}
      <section id="tools" className="mb-16">
        <SectionLabel>Small Tools</SectionLabel>
        <p className="text-jax-body text-sm leading-relaxed mb-6">
          Single file HTML tools I built to solve specific problems. No frameworks, no build step.
        </p>
        <div className="portfolio-project-list">
          {smallTools.map((tool) => (
            <ProjectCard key={tool.name} {...tool} />
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Learning Notes ────────────────────────────────────── */}
      <section id="notes" className="mb-16">
        <SectionLabel>Learning Notes</SectionLabel>
        <p className="text-jax-body text-sm leading-relaxed mb-6">
          HTML explainers for interesting concepts.
        </p>
        <div className="portfolio-project-list">
          {notes.map((note) => (
            <a
              key={note.slug}
              href={note.slug}
              target="_blank"
              rel="noopener noreferrer"
              className="portfolio-project-row is-clickable"
            >
              <div className="portfolio-project-heading">
                <h3>{note.title}</h3>
                <span className="portfolio-project-action" aria-hidden="true">Read note</span>
              </div>
              <p className="portfolio-project-summary">{note.summary}</p>
              <ul className="portfolio-project-stack">
                <li>{note.date}</li>
                {note.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            </a>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Links ─────────────────────────────────────────────── */}
      <section id="links" className="mb-16">
        <SectionLabel>Links</SectionLabel>
        <div className="border border-jax-navy bg-jax-surface rounded-lg overflow-hidden px-5">
          {socialLinks.map((link) => (
            <LinkItem key={link.label} {...link} />
          ))}
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="border-t border-jax-navy pt-8 flex items-center justify-between">
        <p className="text-[11px] font-mono text-jax-subtle">
          © {new Date().getFullYear()} Jax
        </p>
        {/* <p className="text-[11px] font-mono text-jax-subtle">
          built with <span className="text-jax-link">Cursor</span>
        </p> */}
      </footer>
      </div>
    </main>

    <EagleBackToTop visible={showScrollTop} />
  </div>
  )
}

export default About
